import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../components/layout/MainLayout";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import "./UpgradePlans.css";

const formatMoney = (amount) => `Rs. ${Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function PaymentHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [billingHistory, setBillingHistory] = useState([]);

  const fetchBillingHistory = useCallback(async () => {
    try {
      const response = await api.get("/profile");
      setBillingHistory(response.data?.data?.billing_history || []);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to load billing history",
        text: error.response?.data?.message || "Please refresh and try again.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && !user.is_owner) {
      navigate("/dashboard", { replace: true });
      return;
    }

    fetchBillingHistory();
  }, [user, navigate, fetchBillingHistory]);

  const handleDownloadInvoice = async (planId) => {
    try {
      const res = await api.get(`/profile/invoice/${planId}`, {
        responseType: "blob",
      });

      const disposition = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"] || "";
      const fileNameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const downloadName = fileNameMatch?.[1] || `Invoice-${planId}.pdf`;
      const fileURL = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(fileURL);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Invoice download failed",
        text: error.response?.data?.message || "Failed to download invoice.",
      });
    }
  };

  const activePlansCount = billingHistory.filter((bill) => bill.status === "Active").length;
  const totalBilledAmount = billingHistory.reduce((sum, bill) => sum + Number(bill.amount || 0), 0);
  const highestPeakEmployees = billingHistory.reduce(
    (highest, bill) => Math.max(highest, Number(bill.peak_employee_count || 0)),
    0,
  );

  return (
    <MainLayout>
      <div className="upgrade-page">
        <div className="upgrade-detail-shell">
          <div className="upgrade-detail-header">
            <div>
              <span className="upgrade-kicker">Pricing & Subscription</span>
              <h2>Payment History</h2>
              <p>Review billed plans, peak employee counts, and download invoice PDFs whenever you need them.</p>
            </div>
            <button type="button" className="upgrade-back-btn" onClick={() => navigate("/profile")}>
              Back to Profile
            </button>
          </div>

          <div className="payment-history-card">
            {loading ? (
              <div className="upgrade-loading-card">
                <div className="spinner"></div>
                <p>Loading payment history...</p>
              </div>
            ) : billingHistory.length === 0 ? (
              <div className="payment-history-empty">
                <i className="fa-solid fa-file-invoice-dollar"></i>
                <p>No billing history found yet.</p>
              </div>
            ) : (
              <div className="payment-history-content">
                <div className="payment-history-summary">
                  <div className="payment-history-metric">
                    <span>Invoices</span>
                    <strong>{billingHistory.length}</strong>
                  </div>
                  <div className="payment-history-metric">
                    <span>Active Plans</span>
                    <strong>{activePlansCount}</strong>
                  </div>
                  <div className="payment-history-metric">
                    <span>Total Billed</span>
                    <strong>{formatMoney(totalBilledAmount)}</strong>
                  </div>
                  <div className="payment-history-metric">
                    <span>Highest Peak</span>
                    <strong>{highestPeakEmployees}</strong>
                  </div>
                </div>

                <div className="payment-history-table-wrap">
                  <table className="payment-history-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Plan</th>
                        <th>Amount</th>
                        <th>Included</th>
                        <th>Add-on Rate</th>
                        <th>Peak Employees</th>
                        <th>Ceiling</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Status</th>
                        <th>Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingHistory.map((bill, index) => (
                        <tr key={bill.id}>
                          <td>{index + 1}</td>
                          <td>
                            <div className="payment-plan-name">{bill.plan_name || "N/A"}</div>
                            {bill.plan_detail ? (
                              <div className="payment-plan-detail">{bill.plan_detail}</div>
                            ) : null}
                          </td>
                          <td className="payment-amount-cell">{formatMoney(bill.amount)}</td>
                          <td>{bill.included_employees ?? "-"}</td>
                          <td>{formatMoney(bill.add_on_rate)}</td>
                          <td>{bill.peak_employee_count ?? "-"}</td>
                          <td>{bill.employee_ceiling ?? "-"}</td>
                          <td>{bill.start_date ? new Date(bill.start_date).toLocaleDateString("en-IN") : "-"}</td>
                          <td>{bill.end_date ? new Date(bill.end_date).toLocaleDateString("en-IN") : "-"}</td>
                          <td>
                            <span className={`payment-status ${bill.status === "Active" ? "active" : "expired"}`}>
                              {bill.status}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="invoice-download-btn"
                              onClick={() => handleDownloadInvoice(bill.id)}
                              title="Download Invoice PDF"
                              aria-label={`Download invoice for ${bill.plan_name || "plan"}`}
                            >
                              <i className="fa-solid fa-file-arrow-down"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default PaymentHistory;
